import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useUploads } from "@/context/UploaderContext";
import { useAuth } from "@/context/AuthContext";
import { WhoCanSee } from "./WhoCanSee";

const AlbumRow = ({ g, i, onChange }) => (
  <li className="flex items-center gap-3 py-2 border-b border-neutral-200" data-testid={`folder-album-row-${i}`}>
    <input type="checkbox" checked={g.on} onChange={() => onChange({ on: !g.on })} data-testid={`folder-album-tick-${i}`} />
    <input className="flex-1 min-w-0 border border-neutral-300 px-2 py-1 text-sm" value={g.name} maxLength={120} disabled={!g.on}
      onChange={(e) => onChange({ name: e.target.value })} data-testid={`folder-album-name-${i}`} />
    <span className="text-xs text-neutral-600 shrink-0" data-testid={`folder-album-count-${i}`}>{g.indices.length} photos/videos</span>
  </li>
);

// Preview "Album — N photos/videos" per folder, then upload each folder into its album
export const FolderUploadDialog = ({ picked, dest, onClose }) => {
  const { add } = useUploads();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [groups, setGroups] = useState([]);
  const [space, setSpace] = useState(dest === "admin" ? "family" : dest);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setPlan(null);
    if (!picked) return;
    api.post("/uploads/folder/plan", { paths: picked.map((p) => p.path) })
      .then(({ data }) => { setPlan(data); setGroups(data.groups.map((g) => ({ ...g, on: true }))); })
      .catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [picked]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = groups.filter((g) => g.on && g.name.trim());
  const total = chosen.reduce((n, g) => n + g.indices.length, 0);
  const skipped = Object.entries(plan?.skipped || {});

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/folder/albums", { space, names: chosen.map((g) => g.name.trim()) });
      chosen.forEach((g) => {
        const name = g.name.trim().slice(0, 120);
        add(g.indices.map((i) => picked[i].file), space, data.albums[name].id, name);
      });
      toast.success(`Uploading ${total} file${total === 1 ? "" : "s"} into ${chosen.length} album${chosen.length === 1 ? "" : "s"}`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!picked} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="folder-upload-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl flex items-center gap-2"><FolderOpen size={20} /> Upload folder</DialogTitle>
          <DialogDescription>Each folder becomes an album. Rename or untick any before uploading.</DialogDescription>
        </DialogHeader>
        {!plan ? <div className="flex justify-center py-6"><Loader2 className="animate-spin" /></div> : plan.needs_onedrive ? (
          <p className="text-sm text-red-700" data-testid="folder-needs-onedrive">
            Connect OneDrive first. {user?.role === "admin"
              ? <Link to="/settings?tab=storage" className="underline" onClick={onClose} data-testid="folder-onedrive-settings-link">Open Settings → Storage</Link>
              : "Ask an admin to connect it in Settings → Storage."}
          </p>
        ) : (
          <>
            {groups.length ? <ul className="max-h-72 overflow-y-auto" data-testid="folder-album-list">
              {groups.map((g, i) => <AlbumRow key={i} g={g} i={i} onChange={(p) => setGroups((gs) => gs.map((x, j) => (j === i ? { ...x, ...p } : x)))} />)}
            </ul> : <p className="text-sm" data-testid="folder-empty">No photos or videos found in this folder.</p>}
            {skipped.length > 0 && <p className="text-xs text-neutral-500" data-testid="folder-skipped">Skipping {skipped.map(([r, n]) => `${n} ${r}`).join(" · ")}</p>}
            <WhoCanSee space={space} onChange={setSpace} />
            <div className="flex justify-end gap-2">
              <button className="btn-outline" onClick={onClose} data-testid="folder-cancel-button">Cancel</button>
              <button className="btn-primary inline-flex items-center gap-2" disabled={!total || busy} onClick={start} data-testid="folder-upload-start-button">
                {busy && <Loader2 size={14} className="animate-spin" />}Upload {total}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
